import React, { useState, useEffect } from 'react';
import { AdminDashboard } from './AdminDashboard';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Plus, Edit, Trash2, Save, X, Upload, Image as ImageIcon } from 'lucide-react';
import { adminApi } from '../services/adminApi';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const AdminProjects = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingProject, setEditingProject] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    category: '',
    description: '',
    year: new Date().getFullYear().toString(),
    image: null
  });

  useEffect(() => {
    loadProjects();
  }, []);

  const loadProjects = async () => {
    try {
      const data = await adminApi.getProjects();
      setProjects(data);
    } catch (error) {
      toast.error('Failed to load projects');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (project) => {
    setEditingProject(project.id);
    setFormData({
      title: project.title,
      category: project.category,
      description: project.description,
      year: project.year,
      image: project.image
    });
    setIsCreating(false);
  };

  const handleCreate = () => {
    setIsCreating(true);
    setEditingProject(null);
    setFormData({
      title: '',
      category: '',
      description: '',
      year: new Date().getFullYear().toString(),
      image: null
    });
  };

  const handleCancel = () => {
    setEditingProject(null);
    setIsCreating(false);
    setFormData({ title: '', category: '', description: '', year: new Date().getFullYear().toString(), image: null });
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }

    setUploading(true);
    try {
      const result = await adminApi.uploadProjectImage(file);
      setFormData({ ...formData, image: result.path });
      toast.success('Image uploaded successfully');
    } catch (error) {
      toast.error('Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    try {
      if (!formData.title || !formData.category || !formData.description || !formData.year) {
        toast.error('Please fill all required fields');
        return;
      }

      if (isCreating) {
        await adminApi.createProject(formData);
        toast.success('Project created successfully');
      } else {
        await adminApi.updateProject(editingProject, formData);
        toast.success('Project updated successfully');
      }

      loadProjects();
      handleCancel();
    } catch (error) {
      toast.error('Failed to save project');
    }
  };

  const handleDelete = async (projectId) => {
    if (!window.confirm('Are you sure you want to delete this project?')) return;

    try {
      await adminApi.deleteProject(projectId);
      toast.success('Project deleted successfully');
      loadProjects();
    } catch (error) {
      toast.error('Failed to delete project');
    }
  };

  if (loading) {
    return (
      <AdminDashboard>
        <div className="flex items-center justify-center h-64">
          <p className="text-slate-500">Loading projects...</p>
        </div>
      </AdminDashboard>
    );
  }

  return (
    <AdminDashboard>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Projects Management</h1>
            <p className="text-slate-600 mt-1">Manage your project portfolio</p>
          </div>
          <Button
            onClick={handleCreate}
            className="bg-gradient-to-r from-[#1E4E9E] to-[#00B050] hover:from-[#00B050] hover:to-[#1E4E9E] text-white"
          >
            <Plus className="w-5 h-5 mr-2" />
            Add Project
          </Button>
        </div>

        {/* Create/Edit Form */}
        {(isCreating || editingProject) && (
          <Card className="border-2 border-[#1E4E9E]">
            <CardHeader>
              <CardTitle>{isCreating ? 'Create New Project' : 'Edit Project'}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">Title *</label>
                  <Input
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="Project title"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">Category *</label>
                  <Input
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    placeholder="e.g., Renewable Energy, Technology"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Description *</label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Project description"
                  rows={3}
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Year *</label>
                <Input
                  value={formData.year}
                  onChange={(e) => setFormData({ ...formData, year: e.target.value })}
                  placeholder="2024"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Project Image</label>
                <div className="flex items-center space-x-4">
                  <label className="cursor-pointer">
                    <div className="flex items-center space-x-2 px-4 py-2 border-2 border-dashed border-slate-300 rounded-lg hover:border-[#1E4E9E] transition-colors">
                      <Upload className="w-5 h-5 text-slate-500" />
                      <span className="text-sm text-slate-600">
                        {uploading ? 'Uploading...' : 'Upload Image'}
                      </span>
                    </div>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      className="hidden"
                      disabled={uploading}
                    />
                  </label>
                  {formData.image && (
                    <div className="flex items-center space-x-2">
                      <ImageIcon className="w-5 h-5 text-green-500" />
                      <span className="text-sm text-green-600">Image uploaded</span>
                    </div>
                  )}
                </div>
                {formData.image && (
                  <img
                    src={`${BACKEND_URL}${formData.image}`}
                    alt="Preview"
                    className="mt-4 w-48 h-32 object-cover rounded-lg border"
                  />
                )}
              </div>

              <div className="flex space-x-3">
                <Button onClick={handleSave} className="bg-[#00B050] hover:bg-[#00B050]/90">
                  <Save className="w-4 h-4 mr-2" />
                  Save
                </Button>
                <Button onClick={handleCancel} variant="outline">
                  <X className="w-4 h-4 mr-2" />
                  Cancel
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Projects Grid */}
        <div className="grid md:grid-cols-3 gap-6">
          {projects.map((project) => (
            <Card key={project.id} className="hover:shadow-lg transition-shadow overflow-hidden">
              {/* Project Image */}
              <div className="h-40 bg-gradient-to-br from-[#1E4E9E]/20 to-[#00B050]/20 flex items-center justify-center">
                {project.image ? (
                  <img
                    src={`${BACKEND_URL}${project.image}`}
                    alt={project.title}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <ImageIcon className="w-12 h-12 text-slate-300" />
                )}
              </div>

              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <CardTitle className="text-lg">{project.title}</CardTitle>
                    <p className="text-sm text-slate-500 mt-1">{project.category} • {project.year}</p>
                  </div>
                  <div className="flex space-x-1">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleEdit(project)}
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-red-600 hover:bg-red-50"
                      onClick={() => handleDelete(project.id)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-slate-600 text-sm">{project.description}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </AdminDashboard>
  );
};